import React from 'react';
import { Waivers } from '@prisma/client';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import useSelectWaiver from './useSelectWaiver';
import useAwaitedForm from '../useAwaitedForm';
import useUpdateWaiver from './useUpdateWaiver';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';
import { previewWaiver } from '../../helpers/waivers';
import { getUpdateWaiverNameSchema } from '../../types/waivers';
import useGetWaivers from './useGetWaivers';
import useArchiveWaiver from './useArchiveWaiver';
import useConfirm from '../useConfirm';

function useWaiverTableActions() {
  const openForm = useAwaitedForm();
  const confirm = useConfirm();

  const { user } = useUser<LoadedUser>();
  const { onSelectWaiver } = useSelectWaiver();
  const { data: waivers } = useGetWaivers();
  const { mutation: updateMutation } = useUpdateWaiver();
  const { mutateAsync: archive } = useArchiveWaiver();

  const editWaiver = React.useCallback(
    (waiverId: string) => {
      onSelectWaiver(waiverId);
    },
    [onSelectWaiver],
  );

  const viewWaiverSubmissions = React.useCallback(
    (waiverId: string) => {
      onSelectWaiver(waiverId, { submissions: true });
    },
    [onSelectWaiver],
  );

  const renameWaiver = React.useCallback(
    async (waiver: Pick<Waivers, 'id' | 'name'>) => {
      try {
        const schema = getUpdateWaiverNameSchema(
          waivers?.map(w => w.name) || [],
        );
        const formResult = await openForm({
          form: {
            title: 'Rename Waiver',
            submitButtonText: 'Rename',
            validationSchema: schema,
            initialValues: {
              name: waiver.name,
            },
            inputRows: [
              [
                {
                  inputType: 'text',
                  name: 'name',
                  label: 'Waiver Name',
                  placeholder: 'Enter Waiver Name',
                },
              ],
            ],
          },
        });
        if (!formResult) return;
        const { name } = formResult;
        await updateMutation.mutateAsync({
          waiverId: waiver.id,
          name,
        });
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 409) {
          toast.error('Waiver with this name already exists');
        } else {
          toast.error('Failed to rename waiver');
        }
      }
    },
    [waivers, openForm, updateMutation],
  );

  const onPreviewWaiver = React.useCallback(
    ({
      draftId,
      publishedId,
    }: {
      draftId: string;
      publishedId: string | undefined;
    }) => {
      const waiverId = publishedId || draftId;
      if (!waiverId) return;
      previewWaiver(user?.business, waiverId);
    },
    [user?.business],
  );

  const archiveWaiver = React.useCallback(
    async (waiverId: string, services: number[]) => {
      try {
        const servicesMessage =
          services.length > 0
            ? `\nThere ${services.length === 1 ? 'is' : 'are'} ${
                services.length
              } service${services.length === 1 ? '' : 's'} that use${
                services.length === 1 ? 's' : ''
              } this waiver. If you archive this waiver, no waiver will be used for new bookings of ${
                services.length === 1 ? 'this service' : 'these services'
              }. `
            : '';
        const message = `Are you sure you want to archive this waiver? ${servicesMessage}Any pending waiver requests will still use this waiver. This action cannot be undone.`;

        const confirmed = await confirm({
          title: 'Archive Waiver',
          message,
        });
        if (!confirmed) return;

        await archive(waiverId);
      } catch {
        toast.error('Failed to archive waiver');
      }
    },
    [archive, confirm],
  );

  return {
    editWaiver,
    viewWaiverSubmissions,
    renameWaiver,
    previewWaiver: onPreviewWaiver,
    archiveWaiver,
  };
}

export default useWaiverTableActions;
